import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Icon,
  IdentifierLink,
  LoadingState,
  SideSheet,
  TextArea,
} from '@tessma/design-system';
import type { AiGateway, AskAnswer, Citation, Proposal } from './gateway.js';

/**
 * Ask TESSMA AI.
 *
 * **BUILT FROM THE FROZEN DESIGN, NOT BESIDE IT.** The drawer is `SideSheet` —
 * the right-side sheet §13 froze — and everything inside it is `Card`, `Button`,
 * `Alert`, `TextArea` and `IdentifierLink`. Nothing here introduces a surface,
 * a radius or an elevation of its own, and §14's rule is followed to the letter:
 * AI is teal and restrained, the sparkle is the indicator, and there is no
 * purple, no gradient, no glow and no consumer chatbot styling.
 *
 * **NOTHING ON THIS SCREEN DECIDES WHAT MAY BE SEEN.** It renders what the API
 * returned, and what the API returned went through `accessPredicate` in SQL. A
 * citation is rendered because the server sent it; the component has no list of
 * what to hide and no branch that could grow one.
 *
 * **A REFUSAL IS RENDERED AS AN ANSWER, NOT AS AN ERROR.** The API answers 200
 * with an outcome, and so does this: no budget, a credential in the question, no
 * readable records, an answer that could not be grounded — each gets the
 * gateway's own sentence in an `Alert`, not a red failure banner. Only a
 * genuinely broken call becomes an error, because only that is one.
 *
 * **THERE IS NO STREAMING, AND THAT IS THE DESIGN.** An answer is validated and
 * scored before the caller sees it, and a token that has been streamed cannot be
 * unsent. The drawer shows a pending state instead, which is the honest trade:
 * a slightly later answer that was checked, rather than an immediate one that
 * was not.
 */

export interface AskTessmaAiProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly gateway: AiGateway;
  /** Seeds the box, so a test — or a deep link — can open the drawer mid-question. */
  readonly initialQuestion?: string;
}

interface Turn {
  readonly id: string;
  readonly role: 'user' | 'assistant';
  readonly body: string;
  readonly citations?: readonly Citation[];
  readonly proposals?: readonly Proposal[];
  readonly refusal?: boolean;
}

export function AskTessmaAi({ open, onClose, gateway, initialQuestion = '' }: AskTessmaAiProps) {
  const [question, setQuestion] = useState(initialQuestion);
  const [turns, setTurns] = useState<readonly Turn[]>([]);
  const [conversationId, setConversationId] = useState<string | undefined>(undefined);
  const [pending, setPending] = useState(false);
  const [problem, setProblem] = useState<string | undefined>(undefined);
  const [decided, setDecided] = useState<Record<string, string>>({});

  // A closed drawer holds no thread. Reopening starts fresh rather than
  // resuming something the person may have forgotten they left open — and the
  // history list is where an earlier thread is deliberately found.
  useEffect(() => {
    if (!open) {
      setTurns([]);
      setConversationId(undefined);
      setProblem(undefined);
      setDecided({});
    }
  }, [open]);

  const record = useCallback((answer: AskAnswer, asked: string) => {
    setConversationId(answer.conversationId);
    const assistant: Turn =
      answer.outcome === 'answered'
        ? {
            id: answer.requestId,
            role: 'assistant',
            body: answer.answer,
            citations: answer.citations,
            proposals: answer.proposals,
          }
        : { id: answer.requestId, role: 'assistant', body: answer.message, refusal: true };
    setTurns((existing) => [
      ...existing,
      { id: `${answer.requestId}-q`, role: 'user', body: asked },
      assistant,
    ]);
  }, []);

  const submit = useCallback(
    async (mode: 'ask' | 'act') => {
      const asked = question.trim();
      if (asked.length === 0 || pending) return;
      setPending(true);
      setProblem(undefined);
      try {
        const answer =
          mode === 'act' ? await gateway.act(asked) : await gateway.ask(asked, conversationId);
        record(answer, asked);
        setQuestion('');
      } catch (error) {
        // Only a genuinely broken call lands here. A refusal came back as a
        // 200 with an outcome and was rendered as an answer above.
        setProblem(error instanceof Error ? error.message : 'The request could not be completed.');
      } finally {
        setPending(false);
      }
    },
    [question, pending, gateway, conversationId, record],
  );

  const decide = useCallback(
    async (proposal: Proposal, decision: 'confirm' | 'reject') => {
      setProblem(undefined);
      try {
        if (decision === 'reject') {
          await gateway.reject(proposal.proposalId);
          setDecided((existing) => ({ ...existing, [proposal.proposalId]: 'Rejected' }));
          return;
        }
        const outcome = await gateway.confirm(proposal.proposalId);
        /**
         * THE REFUSED CASE IS SHOWN, NOT SWALLOWED.
         *
         * A confirmation the platform refused is the system working: the
         * reviewer's own permission check said no. Rendering it as success
         * would teach people that confirming always works, which is exactly
         * the habit that makes the next real refusal invisible.
         */
        setDecided((existing) => ({
          ...existing,
          [proposal.proposalId]:
            outcome.executionStatus === 'succeeded'
              ? 'Done'
              : `Not done — ${outcome.detail ?? 'you may not perform this action'}`,
        }));
      } catch (error) {
        setProblem(error instanceof Error ? error.message : 'The decision could not be recorded.');
      }
    },
    [gateway],
  );

  return (
    <SideSheet
      open={open}
      onClose={onClose}
      title="Ask TESSMA AI"
      subtitle="Answers are drawn only from records you can already open."
      footer={
        <div className="ai-drawer__composer">
          {/*
            THE LABEL IS RENDERED AND VISUALLY HIDDEN, rather than omitted.

            §13 freezes the footer as "input + 38px teal send button", with no
            label — and a textarea with no accessible name is unusable with a
            screen reader. `TextArea` requires a label and renders it, so the
            drawer's own stylesheet hides it visually and nothing else changes:
            the frozen metrics are untouched, the accessible name exists, and
            the design system is not modified to suit one screen.
          */}
          <TextArea
            label="Your question"
            rows={2}
            value={question}
            placeholder="Ask about your tasks, records or organisation…"
            onChange={(event) => setQuestion(event.target.value)}
          />
          <div className="ai-drawer__composer-actions">
            <Button
              variant="secondary"
              onClick={() => void submit('act')}
              disabled={pending || question.trim().length === 0}
            >
              Suggest an action
            </Button>
            <Button
              variant="ai"
              icon={<Icon name="sparkle" size={15} />}
              onClick={() => void submit('ask')}
              disabled={pending || question.trim().length === 0}
            >
              Ask
            </Button>
          </div>
        </div>
      }
    >
      {problem !== undefined && (
        <Alert tone="danger" title="Something went wrong">
          {problem}
        </Alert>
      )}

      {turns.length === 0 && !pending && (
        <Card>
          <p className="ai-drawer__intro">
            Ask a question about your work. TESSMA AI reads only the records you could open
            yourself, cites what it used, and says so plainly when it cannot answer.
          </p>
        </Card>
      )}

      {turns.map((turn) =>
        turn.role === 'user' ? (
          <p key={turn.id} className="ai-drawer__question">
            {turn.body}
          </p>
        ) : turn.refusal === true ? (
          <Alert key={turn.id} tone="info" title="I have not answered that">
            {turn.body}
          </Alert>
        ) : (
          <Card key={turn.id}>
            <p className="ai-drawer__answer">{turn.body}</p>

            {turn.citations !== undefined && turn.citations.length > 0 && (
              <div className="ai-drawer__sources">
                <p className="ai-drawer__sources-label">Drawn from</p>
                <ul className="ai-drawer__sources-list">
                  {turn.citations.map((citation) => (
                    <li key={`${citation.resource}:${citation.recordId}`}>
                      {/* A real link to the real record, so a person can check
                          the answer against the thing it came from. */}
                      <IdentifierLink href={`/records/${citation.resource}/${citation.recordId}`}>
                        {citation.title}
                      </IdentifierLink>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {turn.proposals?.map((proposal) => (
              <div key={proposal.proposalId} className="ai-drawer__proposal">
                <p className="ai-drawer__proposal-summary">{proposal.summary}</p>
                {proposal.rationale.length > 0 && (
                  <p className="ai-drawer__proposal-rationale">{proposal.rationale}</p>
                )}
                {/* NAMED BEFORE THE CLICK. A reviewer should know what they are
                    about to exercise; the live check still happens server-side
                    against them, and this is information rather than a control. */}
                <p className="ai-drawer__proposal-permission">
                  Requires <code>{proposal.permissionKey}</code>
                </p>
                {decided[proposal.proposalId] === undefined ? (
                  <div className="ai-drawer__proposal-actions">
                    <Button variant="secondary" onClick={() => void decide(proposal, 'reject')}>
                      No
                    </Button>
                    <Button variant="primary" onClick={() => void decide(proposal, 'confirm')}>
                      Do it
                    </Button>
                  </div>
                ) : (
                  <p className="ai-drawer__proposal-outcome">{decided[proposal.proposalId]}</p>
                )}
              </div>
            ))}
          </Card>
        ),
      )}

      {pending && (
        <div className="ai-drawer__pending">
          {/* `LoadingState` already carries a spinner; adding a second one was
              two spinners for one wait. */}
          <LoadingState
            title="Checking what you can see…"
            description="The answer is validated and scored before it is shown, so it is never streamed half-checked."
            compact
          />
        </div>
      )}
    </SideSheet>
  );
}
